import { FC, memo, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material"
import { Modal } from "@/core/components"
import { getApiErrorMessage } from "@/core/utils"
import { GuestBot } from "../../interfaces"
import {
  useBotsListQuery,
  useCreateMailingMutation,
  useTemplatesListQuery,
} from "../../store"
import type { Notify } from "./notify"

const STEPS = ["Гостевой бот", "Шаблон"]
const FETCH_LIMIT = 200

const TYPE_BADGE: Record<GuestBot.PayloadType, string> = {
  text: "Текст",
  photo: "Фото",
  document: "Документ",
}

interface IProps {
  /** Selected chatIds from the pool table (store selection). */
  chatIds: number[]
  notify: Notify
  onClose: () => void
  /** 201 hook — the pool table clears the store selection + closes the modal (D-15). */
  onCreated: () => void
}

/** 404 body → RU message (delta §8: bot/template missing or NoEligibleClient). */
const describeMailing404 = (e: unknown): string => {
  const body = (
    e as { data?: { message?: unknown; error?: unknown } } | null | undefined
  )?.data
  const raw = [
    typeof body?.message === "string" ? body.message : "",
    typeof body?.error === "string" ? body.error : "",
  ].join(" ")
  // Delta §8 guarantees the NoEligibleClient 404 only NAMES THE ROLE in the
  // body — the class name may not reach the wire, so match the role token
  // ("guest-bot") too. This branch MUST stay first: the executor message
  // itself contains "bot", so the bot-missing check below would win otherwise.
  if (raw.includes("NoEligibleClient") || raw.includes("guest-bot")) {
    return `Рассылка не создана: нет доступного исполнителя для роли guest-bot.\n${getApiErrorMessage(e)}`
  }
  if (/bot/i.test(raw)) return "Бот не найден."
  return "Шаблон не найден. Выберите другой шаблон."
}

/**
 * Вся форма живёт в отдельном компоненте ВНУТРИ Modal (v1 Modal-remount
 * pattern); обёртка — React.memo со стабилизированными пропсами родителя.
 */
const WizardForm: FC<IProps> = ({
  chatIds,
  notify,
  onClose: _onClose,
  onCreated,
}) => {
  const navigate = useNavigate()
  const [step, setStep] = useState(0)
  const [botId, setBotId] = useState<string | null>(null)
  const [botSearch, setBotSearch] = useState("")
  const [templateId, setTemplateId] = useState<string | null>(null)
  const [templateSearch, setTemplateSearch] = useState("")
  const [pauseMsBetweenChats, setPauseMsBetweenChats] = useState(30000)
  const [jitterMaxMs, setJitterMaxMs] = useState(3000)
  const [showExtra, setShowExtra] = useState(false)
  const [submitError, setSubmitError] = useState<{
    message: string
    activeMailingId?: string
  } | null>(null)

  const { data: bots } = useBotsListQuery({
    queryParams: { limit: FETCH_LIMIT },
  })
  const { data: templates } = useTemplatesListQuery({
    queryParams: { limit: FETCH_LIMIT },
  })
  const [createMailing, { isLoading }] = useCreateMailingMutation()

  const botsBlindSpot = !!bots && bots.total > bots.items.length
  const templatesBlindSpot =
    !!templates && templates.total > templates.items.length

  const filteredBots = useMemo(() => {
    const q = botSearch.trim().toLowerCase()
    if (!q) return bots?.items ?? []
    return (bots?.items ?? []).filter(
      (b) =>
        (b.username ?? "").toLowerCase().includes(q) ||
        String(b.botId).includes(q),
    )
  }, [bots, botSearch])
  const filteredTemplates = useMemo(() => {
    const q = templateSearch.trim().toLowerCase()
    if (!q) return templates?.items ?? []
    return (templates?.items ?? []).filter((t) =>
      t.name.toLowerCase().includes(q),
    )
  }, [templates, templateSearch])

  const tempoValid =
    pauseMsBetweenChats >= 0 &&
    pauseMsBetweenChats <= 3_600_000 &&
    jitterMaxMs >= 0 &&
    jitterMaxMs <= 60_000

  const stepValid = [
    !!botId && !botsBlindSpot,
    !!templateId && !templatesBlindSpot && tempoValid,
  ][step]

  const goTo = (next: number) => {
    setSubmitError(null)
    setStep(next)
  }

  const handleSubmit = async () => {
    if (!botId || !templateId || chatIds.length === 0) return
    setSubmitError(null)
    try {
      const res = await createMailing({
        pathParams: { id: botId },
        // Rev 3: body WITHOUT text (delta §4.1 as amended) — the posted
        // message = binding trigger + @username, resolved server-side.
        body: { templateId, chatIds, pauseMsBetweenChats, jitterMaxMs },
      }).unwrap()
      notify("success", "Рассылка запущена")
      navigate(`/telegram/guest-bots/mailings/${botId}/${res.id}`)
      onCreated()
    } catch (e) {
      if (GuestBot.isActiveMailingConflict(e)) {
        setSubmitError({
          message: "У этого бота уже есть активная рассылка",
          activeMailingId: e.data.activeMailingId,
        })
        return
      }
      const status = (e as { status?: number }).status
      if (status === 409) {
        // Username-unknown conflict: standard body WITHOUT activeMailingId
        // (delta §8) — the guard above already returned for the id case.
        setSubmitError({
          message:
            "Неизвестен @username бота: бот ещё не подключался к Telegram.",
        })
        return
      }
      if (status === 404) {
        setSubmitError({ message: describeMailing404(e) })
        return
      }
      // 400 (DTO size/ranges, chatId not in pool, missing binding) and others
      // surface inline via getApiErrorMessage — wizard open, state preserved.
      setSubmitError({ message: getApiErrorMessage(e) })
    }
  }

  return (
    <Stack spacing={2}>
      <Stepper activeStep={step} alternativeLabel>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {step === 0 && (
        <Stack spacing={1}>
          {botsBlindSpot && bots && (
            <Alert severity="warning">
              {`Загружены не все боты (${bots.items.length} из ${bots.total}). Запуск рассылки недоступен.`}
            </Alert>
          )}
          <TextField
            size="small"
            label="Поиск"
            value={botSearch}
            onChange={(e) => setBotSearch(e.target.value)}
          />
          <Box sx={{ maxHeight: 320, overflow: "auto" }}>
            <RadioGroup value={botId ?? ""} onChange={(_, v) => setBotId(v)}>
              {filteredBots.map((bot) => {
                const disabled =
                  bot.status === "banned" || bot.username === null
                return (
                  <Box key={bot.id}>
                    <FormControlLabel
                      value={bot.id}
                      control={<Radio disabled={disabled} />}
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography>
                            {bot.username ? `@${bot.username}` : "—"}
                          </Typography>
                          <Chip
                            size="small"
                            label={
                              bot.status === "working" ? "Включён" : "Выключен"
                            }
                            color={
                              bot.status === "working" ? "success" : "error"
                            }
                          />
                        </Stack>
                      }
                    />
                    {bot.status === "banned" && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ pl: 4 }}
                      >
                        Бот выключен (статус banned): он не сможет ответить на
                        упоминание. Включите бота на странице «Боты».
                      </Typography>
                    )}
                    {bot.username === null && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ pl: 4 }}
                      >
                        Неизвестен @username бота: бот ещё не подключался к
                        Telegram. Подключите бота (статус working), чтобы
                        запускать рассылку.
                      </Typography>
                    )}
                  </Box>
                )
              })}
            </RadioGroup>
          </Box>
        </Stack>
      )}

      {step === 1 && (
        <Stack spacing={1}>
          {templatesBlindSpot && templates && (
            <Alert severity="warning">
              {`Загружены не все шаблоны (${templates.items.length} из ${templates.total}). Запуск рассылки недоступен.`}
            </Alert>
          )}
          <TextField
            size="small"
            label="Поиск"
            value={templateSearch}
            onChange={(e) => setTemplateSearch(e.target.value)}
          />
          <Box sx={{ maxHeight: 320, overflow: "auto" }}>
            <RadioGroup
              value={templateId ?? ""}
              onChange={(_, v) => setTemplateId(v)}
            >
              {filteredTemplates.map((t) => (
                <FormControlLabel
                  key={t.id}
                  value={t.id}
                  control={<Radio />}
                  label={t.name}
                />
              ))}
            </RadioGroup>
          </Box>
          {selectedTemplatePreview(templateId, templates)}
          <Typography variant="caption" color="text.secondary">
            Текст сообщения берётся из триггера выбранной привязки; бот ответит
            шаблоном.
          </Typography>
        </Stack>
      )}

      {/* Below both steps (spec §5.4): collapsed tempo settings. */}
      <Box>
        <Button size="small" onClick={() => setShowExtra((v) => !v)}>
          {showExtra
            ? "Скрыть дополнительные настройки"
            : "Дополнительные настройки"}
        </Button>
        <Collapse in={showExtra}>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Пауза между чатами, мс"
              type="number"
              inputProps={{ min: 0 }}
              value={pauseMsBetweenChats}
              onChange={(e) => {
                if (e.target.value === "") return
                const next = Number(e.target.value)
                if (Number.isNaN(next)) return
                setPauseMsBetweenChats(next)
              }}
              helperText="По умолчанию 30 000 мс. Диапазон 0 – 3 600 000."
            />
            <TextField
              label="Максимальный джиттер, мс"
              type="number"
              inputProps={{ min: 0 }}
              value={jitterMaxMs}
              onChange={(e) => {
                if (e.target.value === "") return
                const next = Number(e.target.value)
                if (Number.isNaN(next)) return
                setJitterMaxMs(next)
              }}
              helperText="По умолчанию 3 000 мс. Диапазон 0 – 60 000."
            />
          </Stack>
        </Collapse>
      </Box>

      {submitError && (
        <Alert severity="error" sx={{ whiteSpace: "pre-line" }}>
          {submitError.message}
          {submitError.activeMailingId && (
            <Box mt={1}>
              <Button
                size="small"
                variant="contained"
                onClick={() =>
                  navigate(
                    `/telegram/guest-bots/mailings/${botId ?? ""}/${submitError.activeMailingId}`,
                  )
                }
              >
                Открыть рассылку
              </Button>
            </Box>
          )}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" mt={1}>
        <Button disabled={step === 0} onClick={() => goTo(step - 1)}>
          Назад
        </Button>
        {step < STEPS.length - 1 ? (
          <Button
            variant="contained"
            disabled={!stepValid}
            onClick={() => goTo(step + 1)}
          >
            Далее
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={!stepValid || isLoading}
            onClick={handleSubmit}
          >
            Запустить
            {isLoading && <CircularProgress size={18} sx={{ ml: 1 }} />}
          </Button>
        )}
      </Box>
    </Stack>
  )
}

/** Payload preview marked «Ответ бота» — the payload is the BOT's reply,
 *  never posted by the userbot (spec §5.4). */
const selectedTemplatePreview = (
  templateId: string | null,
  templates: GuestBot.TemplatesListRes | undefined,
) => {
  const template = templates?.items.find((t) => t.id === templateId)
  if (!template) return null
  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip size="small" variant="outlined" label="Ответ бота" />
        <Chip size="small" label={TYPE_BADGE[template.payload.type]} />
        <Typography variant="body2">{template.payload.text ?? "—"}</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {template.payload.media ? "Есть медиа" : "Без медиа"}
        {` · Кнопок: ${template.payload.buttons?.length ?? 0}`}
      </Typography>
    </Stack>
  )
}

export const MailingWizard: FC<IProps> = memo(function MailingWizard(props) {
  const { onClose } = props

  return (
    <Modal title="Новая рассылка" onClose={onClose} disableBackdropClose>
      <WizardForm {...props} />
    </Modal>
  )
})
